// Notifications through the browser's push service, turned on one browser at a time (see api/push.js, public/push-worker.js).
import { Get, Post, Send } from './RemoteAuthority';

// Whether this browser can do it at all
export const PushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Throws the error of an answer that says it failed; else the answer
const checked = (result) => {
    if (result.ok === false)
    {
        throw new Error(result.error);
    }
    return result;
};

// A key in the form the server gives it (base64url), as the browser takes it
const KeyBytes = (key) => {
    const text = atob(key.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(key.length / 4) * 4, '='));
    return Uint8Array.from(text, character => character.charCodeAt(0));
};

// This browser's subscription as it stands, if any
const Current = async () => {
    const registration = await navigator.serviceWorker.getRegistration('/');
    return registration ? registration.pushManager.getSubscription() : null;
};

// Whether notifications are on in this browser for whoever is signed in: allowed by the browser as it stands now, and
// known to the server
export const PushIsOn = async () => {
    const subscription = Notification.permission === 'granted' ? await Current() : null;
    if (!subscription)
    {
        return false;
    }
    const { endpoints } = checked(await Get('/games/push'));
    return endpoints.includes(subscription.endpoint);
};

// Turns notifications on in this browser, asking its permission if it hasn't been given. Always a new subscription, as one
// made with other keys (a database started over) can't be used again.
export const TurnPushOn = async () => {
    const permission = await Notification.requestPermission();
    if (permission === 'denied')
    {
        throw new Error("Your browser is blocking notifications from this site. To turn them on, allow them in the browser's settings for this site, then try again.");
    }
    if (permission !== 'granted')
    {
        throw new Error("Notifications are still off, as the browser wasn't given permission to show them.");
    }
    const { key } = checked(await Get('/games/push'));
    await navigator.serviceWorker.register('/push-worker.js');
    const registration = await navigator.serviceWorker.ready;
    await (await registration.pushManager.getSubscription())?.unsubscribe();
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: KeyBytes(key) });
    checked(await Post('/games/push', subscription.toJSON()));
};

// Calls lost when the browser's permission is taken away (in its settings, say) while the page is open; returns a function
// that stops listening. Not every browser can tell: those find out when the page is next opened (PushIsOn).
export const WatchPushPermission = (lost) => {
    let status = null;
    let stopped = false;
    const changed = () => {
        if (status.state !== 'granted') lost();
    };
    navigator.permissions?.query({ name: 'notifications' }).then((found) => {
        if (stopped) return;
        status = found;
        status.addEventListener('change', changed);
    }).catch(() => {});
    return () => {
        stopped = true;
        status?.removeEventListener('change', changed);
    };
};

// Turns them off in this browser
export const TurnPushOff = async () => {
    const subscription = await Current();
    if (subscription)
    {
        checked(await Send('DELETE', '/games/push', { endpoint: subscription.endpoint }));
        await subscription.unsubscribe();
    }
};
